import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleAuthButton from '../auth/GoogleAuthButton';
import useUserStore from '../../stores/userStore';
import { fetchEventByCode, joinParty } from '../../services/eventLifecycle';
import { fetchJoinRequest, watchJoinRequest } from '../../services/joinRequests';
import { normalizeEventCode } from '../../utils/eventCode';
import { enterPartyAfterApproval } from '../../utils/approvedJoinRetry';
import {
  JOIN_REQUEST_STATUS,
  isEventEnded,
  isJoinApprovalRequired,
} from '../../utils/eventStatus';

/**
 * Direct join surface for `/join/{eventCode}` (link and QR entry point).
 *
 * Google sign-in is offered but never required: a name is enough. Guests who do
 * sign in stay guests; role still comes from event.hostId.
 */
export default function JoinScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const eventCode = normalizeEventCode(params.eventCode || '');

  const user = useUserStore((state) => state.user);
  const [event, setEvent] = React.useState(null);
  const [status, setStatus] = React.useState('loading');
  const [joinStatus, setJoinStatus] = React.useState(null);
  const [name, setName] = React.useState('');
  const [joining, setJoining] = React.useState(false);
  /** idle | entering | failed — post-approval enter state */
  const [approvalEnterState, setApprovalEnterState] = React.useState('idle');
  const [approvalEnterNonce, setApprovalEnterNonce] = React.useState(0);

  React.useEffect(() => {
    if (user?.displayName) setName((current) => current || user.displayName);
  }, [user?.displayName]);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!eventCode) {
        setStatus('not-found');
        return;
      }

      try {
        const found = await fetchEventByCode(eventCode);
        if (cancelled) return;

        if (!found) {
          setEvent(null);
          setStatus('not-found');
          return;
        }

        setEvent(found);
        if (isEventEnded(found)) {
          setStatus('ended');
          return;
        }
        setStatus('ready');
      } catch (error) {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventCode]);

  React.useEffect(() => {
    if (!event?.id || !user?.uid || !isJoinApprovalRequired(event)) return undefined;

    let cancelled = false;
    fetchJoinRequest(event.id, user.uid).then((request) => {
      if (cancelled || !request) return;
      setJoinStatus(request.status);
    });

    const stop = watchJoinRequest(event.id, user.uid, (request) => {
      if (request?.status) setJoinStatus(request.status);
    });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [event, user?.uid]);

  React.useEffect(() => {
    if (joinStatus !== JOIN_REQUEST_STATUS.approved || joining) return undefined;

    let cancelled = false;
    setApprovalEnterState('entering');

    (async () => {
      const outcome = await enterPartyAfterApproval({
        joinParty,
        eventCode,
        displayName: name,
      });
      if (cancelled) return;

      if (outcome.ok && outcome.result && !outcome.result.pending) {
        navigate(`/party/${outcome.result.eventData.id}`);
        return;
      }

      if (outcome.result?.pending && outcome.result.joinStatus === JOIN_REQUEST_STATUS.pending) {
        setJoinStatus(JOIN_REQUEST_STATUS.pending);
        setApprovalEnterState('idle');
        return;
      }

      if (outcome.error?.code === 'event-ended') {
        setStatus('ended');
        return;
      }
      if (outcome.error?.code === 'join-denied') {
        setJoinStatus(JOIN_REQUEST_STATUS.denied);
        return;
      }

      setApprovalEnterState('failed');
    })();

    return () => {
      cancelled = true;
    };
  }, [joinStatus, eventCode, name, joining, navigate, approvalEnterNonce]);

  const retryApprovedEnter = () => {
    setApprovalEnterState('entering');
    setApprovalEnterNonce((value) => value + 1);
  };

  const submitJoin = async (submitEvent) => {
    submitEvent?.preventDefault();
    if (joining) return;

    if (!name.trim() && !user?.displayName) {
      toast.error('Please enter your name');
      return;
    }

    setJoining(true);
    try {
      const result = await joinParty({ eventCode, displayName: name });
      if (result.pending) {
        setJoinStatus(result.joinStatus || JOIN_REQUEST_STATUS.pending);
        setApprovalEnterState('idle');
        return;
      }
      toast.success(`Joined ${result.eventData.title}`);
      navigate(`/party/${result.eventData.id}`);
    } catch (error) {
      if (error?.code === 'event-ended') {
        setStatus('ended');
        return;
      }
      if (error?.code === 'join-denied') {
        setJoinStatus(JOIN_REQUEST_STATUS.denied);
        return;
      }
      toast.error(error?.code === 'not-found' ? 'Event not found' : 'Could not join that party');
    } finally {
      setJoining(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 to-black text-white">
        <p>Finding the party…</p>
      </div>
    );
  }

  if (status === 'ended') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-gray-900 to-black p-4 text-center text-white">
        <h1 className="text-3xl font-bold">Rhythmé</h1>
        <p className="text-gray-400">
          {event?.title ? `${event.title} has ended.` : 'This party has ended.'}
          {' '}Guests can no longer join.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20 transition-colors"
        >
          Go to Rhythmé home
        </button>
      </div>
    );
  }

  if (status !== 'ready') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-gray-900 to-black p-4 text-center text-white">
        <h1 className="text-3xl font-bold">Rhythmé</h1>
        <p className="text-gray-400">
          {status === 'not-found'
            ? `We couldn't find a party with the code ${eventCode || '—'}.`
            : 'Something went wrong looking up that party.'}
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20 transition-colors"
        >
          Go to Rhythmé home
        </button>
      </div>
    );
  }

  if (joinStatus === JOIN_REQUEST_STATUS.denied) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-gray-900 to-black p-4 text-center text-white">
        <h1 className="text-3xl font-bold">Rhythmé</h1>
        <p className="text-gray-400">The host declined your request to join {event.title}.</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20 transition-colors"
        >
          Go to Rhythmé home
        </button>
      </div>
    );
  }

  if (joinStatus === JOIN_REQUEST_STATUS.pending || joinStatus === JOIN_REQUEST_STATUS.approved) {
    const approved = joinStatus === JOIN_REQUEST_STATUS.approved;
    const failed = approved && approvalEnterState === 'failed';

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-gray-900 to-black p-4 text-center text-white">
        <h1 className="text-3xl font-bold">
          {approved ? "You're approved. Joining the party…" : 'Waiting for host approval…'}
        </h1>
        <p className="max-w-sm text-gray-400">
          {failed
            ? "Your request was approved, but the party hasn't opened yet. Try again or refresh the page."
            : approved
              ? `Entering ${event.title}…`
              : `The host has received your request to join ${event.title}.`}
        </p>
        {failed && (
          <button
            type="button"
            onClick={retryApprovedEnter}
            className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 transition-colors"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rhythm-page flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
      <div className="rhythm-page-shell rhythm-page-shell--join space-y-6" data-testid="join-page-shell">
        <header className="text-center">
          <p className="text-sm uppercase tracking-widest text-gray-500">Rhythmé</p>
          <h1 className="rhythm-page-title mt-1 font-bold text-white">Join {event.title}</h1>
          {isJoinApprovalRequired(event) && (
            <p className="mt-2 text-sm text-gray-400">The host will need to approve your request.</p>
          )}
        </header>

        <form onSubmit={submitJoin} className="space-y-3">
          <div>
            <label htmlFor="guestName" className="block text-sm font-medium text-gray-300 mb-1">
              Your name
            </label>
            <input
              id="guestName"
              type="text"
              value={name}
              onChange={(changeEvent) => setName(changeEvent.target.value)}
              placeholder="Enter your name"
              className="w-full rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={joining}
            className="w-full rounded-lg bg-purple-600 py-3 font-semibold text-white hover:bg-purple-700 transition-colors disabled:opacity-60"
          >
            {joining
              ? 'Joining…'
              : isJoinApprovalRequired(event)
                ? 'Request to join'
                : 'JOIN PARTY'}
          </button>
        </form>

        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-xs uppercase tracking-wide text-gray-500">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="space-y-2">
          <GoogleAuthButton onSuccess={() => submitJoin()} />
          <p className="text-center text-xs text-gray-500">
            Share your music taste for better music curation.
          </p>
        </div>
      </div>
    </div>
  );
}
